import React, { useState, useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
import { device } from "../../lib/breakpoints";
import { marketingSectionHeading } from "../../styles/marketingSectionTypography";
import ResponsiveDashboardImage from "../../components/home/ResponsiveDashboardImage";
import {
  SearchOutlined,
  MessageOutlined,
  DollarOutlined,
  ToolOutlined,
  FileExcelOutlined,
  BarChartOutlined,
  SwapOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";

const FEATURES = [
  {
    id: "portfolio-overview",
    pickerLabel: "Portfolio overview",
    icon: <BarChartOutlined />,
    title: "Asset Verification at a glance",
    description:
      "Verified rent status, Automated Triage & Dispatch signals, and key investor metrics in one shielded workspace. Your Rental Command Center, not another silo.",
    desktopSrc: "/images/dashboards-desktop/dashboard-desktop.png",
    mobileSrc: "/images/dashboards-mobile/dashboard-mobile.png",
    alt: "LandlordOps dashboard: verified portfolio metrics and rent status",
    demoLink: null,
  },
  {
    id: "deal-analyzer",
    pickerLabel: "Deal analyzer",
    icon: <ThunderboltOutlined />,
    title: "Leo validates deals in seconds, not spreadsheets",
    description:
      "Speed wins in competitive markets. Leo returns investor-grade checks in about 20 seconds instead of 20 minutes of manual digging. Spend less time ruling out bad fits and more on offers that pencil.",
    desktopSrc: "/images/dashboards-desktop/deal-analyzer-desktop.png",
    mobileSrc: "/images/dashboards-mobile/deal-analyzer-mobile.png",
    alt:
      "Deal analyzer: Leo AI returns fast investor-grade validation on a property",
    demoLink: null,
  },
  {
    id: "prescreen",
    pickerLabel: "Pre-screen tenants",
    icon: <SearchOutlined />,
    title: "Pre-screen tenants automatically",
    description:
      "Leo AI qualifies prospects on your public listing pages before they reach you. Credit, income, pets, and move-in date. Only qualified leads get through. Automated screening on every plan.",
    desktopSrc: "/images/dashboards-desktop/listing-prescreen-desktop.png",
    mobileSrc: "/images/dashboards-mobile/listing-prescreen-mobile.png",
    alt: "Public listing page: Leo AI chat widget pre-screening prospects",
    demoLink: "https://landlordops.com/lease/OvtmRRvDEi0UJNYA8iFm",
  },
  {
    id: "leo-tenant",
    pickerLabel: "AI First Responder",
    icon: <MessageOutlined />,
    title: "Your AI First Responder",
    description:
      "100% of tenant noise is triaged by Leo AI before reaching your phone. Rent, policies, and requests stay in OpsChat first. Verified replies and audit-friendly threads, not after-hours pings.",
    desktopSrc: "/images/dashboards-desktop/context-stream-desktop.png",
    mobileSrc: "/images/dashboards-mobile/context-stream-mobile.png",
    alt:
      "AI First Responder: Leo AI triaging tenant messages before the landlord phone",
    demoLink: null,
  },
  {
    id: "rent",
    pickerLabel: "Rent collection",
    icon: <DollarOutlined />,
    title: "Automated rent collection and tracking",
    description:
      "Verified rent reminders, live payment status, and clear paid or overdue views. Stripe-backed records that strengthen your Closing Vault without chasing noise.",
    desktopSrc: "/images/dashboards-desktop/rent-dashboard-desktop.png",
    mobileSrc: "/images/dashboards-mobile/rent-dashboard-mobile.png",
    alt: "Rent dashboard: payment tracking and automated reminders",
    demoLink: null,
  },
  {
    id: "maintenance",
    pickerLabel: "Automated Triage & Dispatch",
    icon: <ToolOutlined />,
    title: "Automated Triage & Dispatch",
    description:
      "Leo AI diagnoses the issue, confirms lease responsibility, and only alerts a vendor if necessary. Midnight tickets become verified work orders with scope, not random texts on your lock screen.",
    desktopSrc: "/images/dashboards-desktop/repairs-dashboard-desktop.png",
    mobileSrc: "/images/dashboards-mobile/repairs-dashboard-mobile.png",
    alt:
      "Automated Triage and Dispatch: verified repair tickets and vendor coordination",
    demoLink: null,
  },
  /*
  {
    id: "vendor-bids",
    pickerLabel: "Vendor bids & negotiation",
    icon: <SwapOutlined />,
    title: "Verified vendor bids with Leo AI",
    description:
      "Leo AI reaches out to vendors, collects bids, and negotiates toward fair pricing while you approve the outcome. Clear scope from Automated Triage & Dispatch. You compare responses and green-light who gets the work.",
    desktopSrc: "/images/dashboards-desktop/repairs-dashboard-desktop.png",
    mobileSrc: "/images/dashboards-mobile/repairs-dashboard-mobile.png",
    alt: "Repair and vendor workflow: bids, negotiation, and maintenance tracking",
    demoLink: null,
  },
  */
  {
    id: "taxes",
    pickerLabel: "Schedule E & taxes",
    icon: <FileExcelOutlined />,
    title: "Audit-ready Schedule E export",
    description:
      "Verified payment tagging. Generate tax-ready Schedule E and financial summaries in one click. Export for your CPA or your Closing Vault. Automated categorization you can defend in diligence.",
    desktopSrc: "/images/dashboards-desktop/taxes-dashboard-desktop.png",
    mobileSrc: "/images/dashboards-mobile/taxes-dashboard-mobile.png",
    alt: "Tax summaries: Schedule E export and financial reporting",
    demoLink: null,
  },
];

const ComprehensiveFeatures = () => {
  const [active, setActive] = useState(0);
  const chipRefs = useRef([]);
  const feature = FEATURES[active];

  useEffect(() => {
    const el = chipRefs.current[active];
    if (!el) return;
    el.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [active]);

  const onChipKeyDown = useCallback((e, index) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setActive(index);
    }
    const go = (next) => {
      e.preventDefault();
      setActive(next);
      requestAnimationFrame(() => chipRefs.current[next]?.focus());
    };
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      go(Math.min(index + 1, FEATURES.length - 1));
    }
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      go(Math.max(index - 1, 0));
    }
    if (e.key === "Home") go(0);
    if (e.key === "End") go(FEATURES.length - 1);
  }, []);

  return (
    <Section id="product-features">
      <Inner>
        <Header>
          <SectionTitle>Inside the platform</SectionTitle>
          <SectionSubtitle>
            The Closing Vault: verified AI triage, Automated Triage &amp;
            Dispatch, investor analytics, and audit-ready exports. Explore each
            layer below.
          </SectionSubtitle>
        </Header>

        <PickerWrap>
          <PickerLabel id="feature-picker-label">
            Explore product areas
          </PickerLabel>
          <PickerScroller role="tablist" aria-labelledby="feature-picker-label">
            {FEATURES.map((f, index) => {
              const isActive = index === active;
              return (
                <FeatureChip
                  key={f.id}
                  ref={(el) => {
                    chipRefs.current[index] = el;
                  }}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`feature-panel-${f.id}`}
                  id={`feature-tab-${f.id}`}
                  tabIndex={isActive ? 0 : -1}
                  $active={isActive}
                  onClick={() => setActive(index)}
                  onKeyDown={(e) => onChipKeyDown(e, index)}
                >
                  <ChipIcon $active={isActive}>{f.icon}</ChipIcon>
                  <ChipText>{f.pickerLabel}</ChipText>
                </FeatureChip>
              );
            })}
          </PickerScroller>
        </PickerWrap>

        <Panel
          role="tabpanel"
          id={`feature-panel-${feature.id}`}
          aria-labelledby={`feature-tab-${feature.id}`}
        >
          <PanelInner key={feature.id}>
            <PanelText>
              <PanelIcon>{feature.icon}</PanelIcon>
              <PanelTitle>{feature.title}</PanelTitle>
              <PanelDescription>{feature.description}</PanelDescription>
              {feature.demoLink ? (
                <DemoWrap>
                  <DemoButton
                    href={feature.demoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Try demo
                  </DemoButton>
                </DemoWrap>
              ) : null}
            </PanelText>
            <PanelVisual>
              <ResponsiveDashboardImage
                desktopSrc={feature.desktopSrc}
                mobileSrc={feature.mobileSrc}
                alt={feature.alt}
              />
            </PanelVisual>
          </PanelInner>
        </Panel>
      </Inner>
    </Section>
  );
};

export default ComprehensiveFeatures;

const Section = styled.section`
  background: var(--color-background);
  padding: 4.5rem 1.25rem 5rem;
  position: relative;

  @media ${device.mobile} {
    padding: 3rem 1rem 3.5rem;
  }
`;

const Inner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2.25rem;

  @media ${device.mobile} {
    margin-bottom: 1.75rem;
  }
`;

const SectionTitle = styled.h2`
  ${marketingSectionHeading}
  color: var(--color-text);
  margin: 0 0 1rem;
`;

const SectionSubtitle = styled.p`
  font-size: 1.05rem;
  line-height: 1.65;
  color: var(--color-text-light);
  max-width: 640px;
  margin: 0 auto;
  font-family: var(--font-secondary);

  @media ${device.mobile} {
    font-size: 1rem;
  }
`;

const PickerWrap = styled.div`
  margin-bottom: 2rem;
`;

const PickerLabel = styled.p`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  margin: 0 0 0.75rem;
  text-align: center;
`;

const PickerScroller = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  row-gap: 0.5rem;
`;

const FeatureChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.95rem;
  border-radius: 999px;
  border: 1.5px solid
    ${(p) => (p.$active ? "var(--color-primary)" : "var(--color-border-light)")};
  background: ${(p) =>
    p.$active ? "var(--color-primary-glow)" : "var(--color-bg-secondary)"};
  color: var(--color-text);
  cursor: pointer;
  font-family: var(--font-secondary);
  font-size: 0.875rem;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;

  @media ${device.mobile} {
    padding: 0.5rem 0.85rem;
    font-size: 0.8125rem;
  }

  &:hover {
    border-color: var(--color-primary-light);
    background: var(--color-primary-glow);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
`;

const ChipIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${(p) =>
    p.$active ? "var(--color-primary-dark)" : "var(--color-primary)"};
  .anticon {
    font-size: 1rem !important;
  }
`;

const ChipText = styled.span`
  line-height: 1.2;
`;

const Panel = styled.div`
  border-radius: 20px;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-light);
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.04),
    0 18px 40px -20px var(--shadow-medium);
  overflow: hidden;
`;

const PanelInner = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  animation: featureFadeIn 0.35s ease;

  @keyframes featureFadeIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media ${device.minTablet} {
    grid-template-columns: minmax(260px, 340px) 1fr;
    gap: 0;
    align-items: stretch;
  }
`;

const PanelText = styled.div`
  padding: 1.75rem 1.5rem;
  text-align: center;

  @media ${device.minTablet} {
    text-align: left;
    padding: 2rem 1.75rem;
    border-right: 1px solid var(--color-border-light);
    background: linear-gradient(
      180deg,
      var(--color-bg-secondary) 0%,
      var(--color-accent) 100%
    );
  }

  @media ${device.mobile} {
    padding: 1.5rem 1.25rem 1.25rem;
  }
`;

const PanelIcon = styled.div`
  font-size: 2.25rem;
  color: var(--color-primary);
  display: flex;
  justify-content: center;
  margin-bottom: 0.65rem;

  .anticon {
    font-size: 2.25rem !important;
    color: var(--color-primary) !important;
  }

  @media ${device.minTablet} {
    justify-content: flex-start;
  }
`;

const PanelTitle = styled.h3`
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0 0 0.65rem;
  font-family: var(--font-primary);
  line-height: 1.3;

  @media ${device.mobile} {
    font-size: 1.2rem;
  }
`;

const PanelDescription = styled.p`
  font-size: 1rem;
  line-height: 1.65;
  color: var(--color-text-light);
  margin: 0;
  font-family: var(--font-secondary);
`;

const DemoWrap = styled.div`
  margin-top: 1.25rem;

  @media ${device.mobile} {
    display: flex;
    justify-content: center;
  }
`;

const DemoButton = styled.a`
  background: linear-gradient(90deg, #2c5f2d 0%, #4a7c59 100%);
  color: #fff;
  padding: 0.65rem 1.35rem;
  border-radius: 999px;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(44, 95, 45, 0.22);
  font-family: "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(44, 95, 45, 0.3);
  }
`;

const PanelVisual = styled.div`
  padding: 0 1rem 1.25rem;

  @media ${device.minTablet} {
    padding: 1.25rem 1.25rem 1.25rem 0;
  }

  @media ${device.mobile} {
    padding: 0 0.75rem 1.5rem;
  }
`;
